"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuthStore } from "@/stores/auth-store";
import {
  wsManager,
  WSMessage,
  MessageHandler,
  ConnectionState,
} from "@/lib/client/websocket-manager";

/**
 * WebSocket Context Interface
 */
interface WebSocketContextType {
  /** Subscribe to a topic, returns unsubscribe function */
  subscribe: (topic: string, handler: MessageHandler) => () => void;
  /** Send a message to the WebSocket server */
  send: (message: WSMessage) => boolean;
  /** Whether WebSocket is currently connected */
  isConnected: boolean;
  /** Current connection state */
  connectionState: ConnectionState;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

/**
 * WebSocket Provider
 *
 * Manages WebSocket connection lifecycle based on authentication state.
 * - Connects when user is authenticated
 * - Disconnects on logout
 * - Handles token refresh
 */
export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuthStore();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>("disconnected");

  // Connect/disconnect based on auth state
  useEffect(() => {
    if (isAuthenticated && user?.token) {
      wsManager.connect(user.token);
    } else {
      wsManager.disconnect();
    }

    return () => {
      // Don't disconnect on unmount if still authenticated
      // The manager handles cleanup on logout via auth-store
    };
  }, [isAuthenticated, user?.token]);

  // Track connection state
  useEffect(() => {
    const unsubscribe = wsManager.onConnectionChange((connected) => {
      setIsConnected(connected);
      setConnectionState(wsManager.state);
    });

    return unsubscribe;
  }, []);

  // Update token when refreshed
  useEffect(() => {
    if (user?.token && isConnected) {
      wsManager.updateToken(user.token);
    }
  }, [user?.token, isConnected]);

  // Memoized subscribe function
  const subscribe = useCallback(
    (topic: string, handler: MessageHandler): (() => void) => {
      return wsManager.subscribe(topic, handler);
    },
    []
  );

  // Memoized send function
  const send = useCallback((message: WSMessage): boolean => {
    return wsManager.send(message);
  }, []);

  // Context value
  const contextValue = useMemo(
    () => ({
      subscribe,
      send,
      isConnected,
      connectionState,
    }),
    [subscribe, send, isConnected, connectionState]
  );

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * Hook to access WebSocket context
 */
export function useWebSocket(): WebSocketContextType {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within WebSocketProvider");
  }
  return context;
}

/**
 * Hook to check WebSocket connection status
 */
export function useWebSocketStatus(): {
  isConnected: boolean;
  connectionState: ConnectionState;
} {
  const context = useContext(WebSocketContext);
  return {
    isConnected: context?.isConnected ?? false,
    connectionState: context?.connectionState ?? "disconnected",
  };
}

// Re-export types for convenience
export type { WSMessage, MessageHandler, ConnectionState };
